import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import {
  SurveyDraft,
  SurveyProgress,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';

// Per-appointment task row returned from `appointmentSurveysList`. The
// patient portal Home card surfaces every outstanding pre-visit item:
// surveys, consents, and the legacy "request-level" steps (Photo ID,
// Insurance card, Insurance, Basic information). Surveys open the slim
// runner here in this app; the rest currently link out to the
// healthcom-app patient request flow until native runners ship.
export type AppointmentTask =
  | {
      kind: 'survey';
      id: string;
      requestId: string;
      language: string;
      title: string;
      // Template id — present so chain-aware UI (e.g. ACCS chain
      // grouping) can collapse repeated entries without string match.
      surveyId: string;
      // Lets the FE detect custom ACCS templates (custom surveyId) for
      // the chain labeling rule.
      behavesAs: string | null;
      createdAt: number;
      started: boolean;
      completedAt: number | null;
      order: number;
    }
  | {
      kind: 'consent';
      id: string;
      requestId: string;
      language: string;
      title: string;
      consentUrl: string | null;
      completedAt: number | null;
      order: number;
    }
  | {
      kind: 'photoId' | 'insuranceCard' | 'insurance' | 'basicInformation';
      requestId: string;
      language: string;
      completedAt: number | null;
      order: number;
    };

// Full PatientSurvey + runtime context returned by the
// `GET /survey/{path}/{patientSurveyId}` endpoint. Drives the runner.
// `validationCode`, `gender`, `age`, `prMap`, and `ccsFollowups` are
// surfaced for CCS / reevaluation surveys, which take over the entire
// surface via the legacy ChiefComplaintSurvey and submit through the
// validationCode-authed whole-survey route on the legacy backend.
export type SurveyDetail = {
  id: string;
  surveyId: string;
  requestId: string;
  validationCode?: string | number | null;
  prMap?: { [key: string]: string };
  ccsFollowups?: string[];
  gender?: number | null;
  age?: number | null;
  language: 'en' | 'es' | string;
  completedAt: number | null;
  draft?: SurveyDraft;
  survey: {
    id: string;
    name: string;
    title: string;
    subtitle: string;
    startMessage: string;
    endMessage: string;
    behavesAs?: string | null;
    allowAdditionalComplaints?: boolean;
    questions: SurveyQuestion[];
    english?: { questions: SurveyQuestion[] };
  };
  currentQuestionId: string | null;
  progress: SurveyProgress;
  // Patient's existing complaints — only populated for reevaluation
  // surveys. <ReevalSurvey> uses these to expand per-complaint
  // follow-up questions before the new-problems prompt.
  complaints?: any[];
};

// Returned by `GET /consent/{path}/{patientConsentId}`.
export type ConsentDetail = {
  id: string;
  requestId: string;
  language: string;
  title: string;
  url: string | null;
  completedAt: number | null;
  completedFile: string | null;
};

// Returned by `GET /request/{path}/{requestId}`.
export type RequestDetail = {
  id: string;
  language: string;
  status: {
    insurance?: number | boolean | { newComm: string };
    uploadPhotoId?: number | boolean | { newComm: string };
    uploadInsuranceCard?: number | boolean | { newComm: string };
    basicInformation?: number | boolean | { newComm: string };
    surveys?: Record<string, unknown>;
    consents?: Record<string, unknown>;
  };
};

const surveyService = () => {
  const listForAppointment = async (
    path: string,
    appointmentId: string
  ): Promise<{ items: AppointmentTask[] }> => {
    return ChiroUpAPI.get('api', `/appointment/${path}/${appointmentId}/surveys`, {});
  };

  const getSurvey = async (path: string, patientSurveyId: string): Promise<SurveyDetail> => {
    return ChiroUpAPI.get('api', `/survey/${path}/${patientSurveyId}`, {});
  };

  const submitAnswer = async ({
    path,
    patientSurveyId,
    patientSurveyQuestionId,
    answer,
  }: {
    path: string;
    patientSurveyId: string;
    patientSurveyQuestionId: string;
    answer: SurveyQuestionAnswer;
  }): Promise<{ question?: SurveyQuestion; progress?: SurveyProgress }> => {
    return ChiroUpAPI.put('api', `/survey/${path}/${patientSurveyId}/${patientSurveyQuestionId}`, {
      body: { patientSurveyQuestionAnswer: answer },
    });
  };

  const getPreviousQuestion = async ({
    path,
    patientSurveyId,
    questionId,
  }: {
    path: string;
    patientSurveyId: string;
    questionId: string;
  }): Promise<{
    question: SurveyQuestion;
    previousQuestion: string;
    progress: SurveyProgress;
  }> => {
    return ChiroUpAPI.get('api', `/survey/${path}/${patientSurveyId}/previous/${questionId}`, {});
  };

  const getConsent = async (path: string, patientConsentId: string): Promise<ConsentDetail> => {
    return ChiroUpAPI.get('api', `/consent/${path}/${patientConsentId}`, {});
  };

  const submitConsent = async ({
    path,
    patientConsentId,
    signature,
  }: {
    path: string;
    patientConsentId: string;
    signature: string | undefined;
  }): Promise<{ completedAt?: number }> => {
    return ChiroUpAPI.put('api', `/consent/${path}/${patientConsentId}`, {
      body: { signature },
    });
  };

  const getRequest = async (path: string, requestId: string): Promise<RequestDetail> => {
    return ChiroUpAPI.get('api', `/request/${path}/${requestId}`, {});
  };

  const s3PostWithRetry = async (url: string, form: FormData): Promise<Response> => {
    try {
      return await fetch(url, { method: 'POST', body: form });
    } catch (_err) {
      await new Promise(r => setTimeout(r, 1000));

      return await fetch(url, { method: 'POST', body: form });
    }
  };

  const uploadFile = async ({
    type,
    requestId,
    file,
    side,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
    file: File;
    side: 'front' | 'back';
  }): Promise<void> => {
    const fileType = file.type.split('/')[1];
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';
    const queryParams: Record<string, string> = {
      fileType,
      side,
    };

    if (type === 'insuranceCard') {
      queryParams.surveyType = 'insuranceCard';
    }

    const res: { fields: { [key: string]: string }; url: string } = await ChiroUpAPI.get(
      'api',
      `/requests/${requestId}/${subPath}`,
      { queryParams }
    );

    const form = new FormData();

    Object.entries(res.fields).forEach(([key, val]) => form.append(key, val));
    form.append('file', file);

    const s3Resp = await s3PostWithRetry(res.url, form);

    if (!s3Resp.ok) {
      throw new Error(`Upload to S3 failed (${s3Resp.status})`);
    }
  };

  const skipUpload = async ({
    type,
    requestId,
  }: {
    type: 'photoId' | 'insuranceCard';
    requestId: string;
  }): Promise<void> => {
    const subPath = type === 'photoId' ? 'upload-photo-id' : 'upload-insurance-card';

    await ChiroUpAPI.get('api', `/requests/${requestId}/${subPath}`, {
      queryParams: {
        skip: 'true',
      },
    });
  };

  return {
    listForAppointment,
    getSurvey,
    submitAnswer,
    getPreviousQuestion,
    getConsent,
    submitConsent,
    getRequest,
    uploadFile,
    skipUpload,
  };
};

export default surveyService();
